import React, { useState, useMemo, useCallback } from 'react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import {
    Clock, AlertTriangle, ArrowRight, BarChart3,
    Settings, X, Filter,
    ThumbsUp, ThumbsDown, Layers, FileText,
    MessageSquare, Activity, Wrench, Truck, Tag, Briefcase, Loader2,
    CheckCircle2, AlertCircle, XCircle
} from 'lucide-react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    PointElement,
    LineElement,
    ArcElement
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DataVisualization from './DataVisualization';
import SearchableDropdown from './SearchableDropdown';
import { useNotification } from '../context/NotificationContext';
import { useMediaQuery } from '../hooks/useMediaQuery';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    PointElement,
    LineElement,
    ArcElement
);

const MI_HEADING_LINE_RE = /^[A-Z][A-Za-z0-9 /&()-]{2,44}:?$/;
const MI_BAR_LINE_RE = /^([A-Za-z0-9][A-Za-z0-9.,% /&()-]{1,60}):\s*([#.=]{5,})\s*\((\d+(?:\.\d+)?)%\)\s*$/;
const MI_BAR_BOLD_LINE_RE = /^\*\*([A-Za-z0-9][A-Za-z0-9.,% /&()-]{1,60})\s*:\*\*\s*([#.=]{5,})\s*\((\d+(?:\.\d+)?)%\)\s*$/;

function flattenText(value) {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string' || typeof value === 'number') return String(value);
    if (Array.isArray(value)) return value.map(flattenText).join('');
    if (React.isValidElement(value)) return flattenText(value.props?.children);
    return '';
}

function parseComparisonBarChildren(children) {
    // First pass: wrapper-agnostic parse from flattened text.
    // ReactMarkdown often wraps list content with <p>, which breaks direct
    // child element parsing.
    const flatRaw = flattenText(children).replace(/\s+/g, ' ').trim();
    const flatNoBullet = flatRaw.replace(/^[\u2022-]\s*/, '').trim();
    const flatNormalized = flatNoBullet.replace(/\*\*/g, '').replace(/`/g, '').trim();
    const flatMatch = flatNormalized.match(/^([A-Za-z0-9][A-Za-z0-9.,% /&()-]{1,60}):\s*([#.=]{5,})\s*\((\d+(?:\.\d+)?)%\)$/);
    if (flatMatch) {
        const [, label, bar, pct] = flatMatch;
        const pctValue = Math.max(0, Math.min(100, Number(pct)));
        if (Number.isFinite(pctValue)) {
            return { label: label.trim(), bar, pctValue };
        }
    }

    // Second pass: structured parse when strong/code are direct children.
    const parts = React.Children.toArray(children);
    let label = '';
    let bar = '';
    let pct = '';
    let trailingText = '';

    for (const part of parts) {
        if (typeof part === 'string' || typeof part === 'number') {
            trailingText += String(part);
            continue;
        }
        if (!React.isValidElement(part)) continue;
        const elementType = typeof part.type === 'string' ? part.type.toLowerCase() : '';
        const text = flattenText(part.props?.children).trim();
        if (!text) continue;

        if (elementType === 'strong') {
            label = text.replace(/:\s*$/, '').trim();
            continue;
        }
        if (elementType === 'code') {
            bar = text;
            continue;
        }
        trailingText += text;
    }

    const pctMatch = trailingText.match(/\((\d+(?:\.\d+)?)%\)/);
    if (pctMatch) pct = pctMatch[1];

    if (!label || !bar || !pct || !/^[#.=]{5,}$/.test(bar)) return null;
    const pctValue = Math.max(0, Math.min(100, Number(pct)));
    if (!Number.isFinite(pctValue)) return null;

    return { label, bar, pctValue };
}

function normalizeMarkdownForDisplay(text) {
    const input = String(text || '').replace(/\r\n/g, '\n').trim();
    if (!input) return input;

    const lines = input.split('\n');
    const normalized = [];

    for (let i = 0; i < lines.length; i += 1) {
        const raw = lines[i];
        const line = raw.trim();
        const next = (lines[i + 1] || '').trim();

        const boldLabel = line.match(/^\*\*([A-Za-z][A-Za-z0-9 /&()-]{2,44})\s*:\*\*$/);
        if (boldLabel) {
            normalized.push(`### ${boldLabel[1].trim()}`);
            continue;
        }

        if (
            MI_HEADING_LINE_RE.test(line) &&
            !line.startsWith('#') &&
            !line.startsWith('- ') &&
            !line.startsWith('* ') &&
            !line.startsWith('|') &&
            next !== ''
        ) {
            normalized.push(`### ${line.replace(/:\s*$/, '').trim()}`);
            continue;
        }

        const lineNoBullet = line.replace(/^-\s+/, '').trim();
        const barMatch = lineNoBullet.match(MI_BAR_BOLD_LINE_RE) || lineNoBullet.match(MI_BAR_LINE_RE);
        if (barMatch) {
            const [, label, bar, pct] = barMatch;
            normalized.push(`- **${label.trim()}:** \`${bar}\` (${pct}%)`);
            continue;
        }

        normalized.push(raw);
    }

    return normalized
        .join('\n')
        .replace(/([^\n])\n(###\s+)/g, '$1\n\n$2')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

const ToolConfigPanel = React.memo(({
    activeTool,
    formData,
    metadata,
    metadataLoading,
    metadataSource,
    loading,
    handleInputChange,
    setFormData,
    handleRunAnalysis
}) => {
    if (!activeTool) return null;
    const toolUsesMetadata = activeTool.inputs.some(input => Boolean(input.metadataKey));

    return (
        <div className="w-full lg:w-[34%] xl:w-[30%] space-y-6 lg:sticky lg:top-24 self-start">
            <div className="mi-config-card bg-white p-4 sm:p-5 rounded-none sm:rounded-2xl border-0 sm:border border-gray-200 shadow-none sm:shadow-sm">
                <div className="flex items-start mb-6 pb-4 border-b border-gray-100">
                    <div className={`p-3 rounded-xl mr-3 shadow-sm border border-white/60 ${activeTool.bgColor}`}>
                        <activeTool.icon className={`h-5 w-5 ${activeTool.color}`} />
                    </div>
                    <div>
                        <div className="inline-flex items-center px-2 py-0.5 mb-1.5 rounded-full text-[10px] font-semibold uppercase tracking-[0.12em] bg-gray-100 text-gray-500">
                            Configuration
                        </div>
                        <h4 className="font-bold text-gray-900 text-[1.35rem] leading-tight tracking-[-0.01em]">{activeTool.title}</h4>
                        <p className="text-sm text-gray-500 mt-1.5 max-w-[22rem] leading-relaxed">
                            {activeTool.description}
                        </p>
                    </div>
                </div>

                <div className="space-y-5">
                    {toolUsesMetadata && metadataLoading && (
                        <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-700 flex items-center gap-2">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Loading filter options from backend...
                        </div>
                    )}
                    {activeTool.inputs.map(input => {
                        const downtimeScope = formData.scope || 'fleet';
                        if (activeTool.id === 'downtime_analysis') {
                            // Scope-aware fields.
                            if (input.name === 'unit_names' && downtimeScope !== 'unit') return null;
                        }
                        return (
                            <div key={input.name} className="group">
                                {!input.metadataKey && (
                                    <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">
                                        {input.label}
                                    </label>
                                )}
                                {input.type === 'select' ? (
                                    <div className="relative">
                                        <select
                                            name={input.name}
                                            value={formData[input.name] || ''}
                                            onChange={handleInputChange}
                                            className="mi-config-input w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none bg-gray-50/50 hover:bg-white transition-colors appearance-none cursor-pointer"
                                        >
                                            {input.options.map(opt => (
                                                <option key={opt} value={opt}>{opt}</option>
                                            ))}
                                        </select>
                                        <div className="absolute right-3 top-3 pointer-events-none text-gray-400">
                                            <Layers className="h-4 w-4" />
                                        </div>
                                    </div>
                                ) : input.metadataKey ? (
                                    (() => {
                                        const metadataOptions = (metadata[input.metadataKey] || [])
                                            .filter(item => {
                                                // Hide PM Service in non-SLA dropdowns.
                                                if (activeTool.id === 'sla_compliance') return true;
                                                return String(item).trim().toLowerCase() !== 'pm service';
                                            })
                                            .map(item => ({ label: item, value: item }));
                                        const metadataPending = metadataLoading && metadataOptions.length === 0;
                                        const metadataRefreshing = metadataLoading && metadataOptions.length > 0;

                                        return (
                                            <>
                                                <SearchableDropdown
                                                    label={input.label}
                                                    placeholder={input.placeholder}
                                                    options={metadataOptions}
                                                    value={formData[input.name] || ''}
                                                    onChange={(val) => setFormData(prev => ({ ...prev, [input.name]: val }))}
                                                    icon={input.icon}
                                                    className="w-full"
                                                    loading={metadataPending}
                                                    disabled={metadataPending}
                                                />
                                                {metadataPending && (
                                                    <p className="text-xs text-blue-600 mt-1.5 flex items-center gap-1.5">
                                                        <Loader2 className="w-3 h-3 animate-spin" />
                                                        Loading filter options from server...
                                                    </p>
                                                )}
                                                {metadataRefreshing && (
                                                    <p className="text-xs text-blue-500 mt-1.5 flex items-center gap-1.5">
                                                        <Loader2 className="w-3 h-3 animate-spin" />
                                                        Refreshing filter options...
                                                    </p>
                                                )}
                                                {metadataSource === 'fallback' && !metadataLoading && (
                                                    <p className="text-xs text-amber-600 mt-1.5">
                                                        Using default options while backend metadata warms up.
                                                    </p>
                                                )}
                                            </>
                                        );
                                    })()
                                ) : (
                                    <input
                                        type={input.type}
                                        name={input.name}
                                        placeholder={input.placeholder}
                                        value={formData[input.name] || ''}
                                        onChange={handleInputChange}
                                        className="mi-config-input w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none bg-gray-50/50 hover:bg-white transition-colors"
                                    />
                                )}
                                {input.hint && (
                                    <p className="text-xs text-gray-500 mt-1">
                                        {input.hint}
                                    </p>
                                )}
                            </div>
                        )
                    })}

                    <button
                        onClick={handleRunAnalysis}
                        disabled={loading}
                        className="mi-run-btn w-full mt-6 text-white font-semibold py-3.5 rounded-xl hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center group"
                    >
                        {loading ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                                Running Analysis...
                            </>
                        ) : (
                            <>
                                Run Analysis
                                <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
});

const ResultsHeaderToolbar = React.memo(({
    result,
    error,
    handleSaveReport,
    onNewAnalysis,
    loading,
    resultFeedback,
    setResultFeedback,
    setShowRandomFeedbackPrompt,
    setShowFeedbackModal,
    apiClient,
    activeTool,
    formData,
    reportStatus,
    setReportStatus
}) => (
    <div className="mi-results-bar mb-4 rounded-none sm:rounded-2xl border-0 sm:border border-gray-200 p-0 sm:p-5 shadow-none sm:shadow-sm">
        <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-xl bg-blue-50 border border-blue-100 shadow-sm">
                <BarChart3 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
                <div className="inline-flex items-center px-2 py-0.5 mb-1 rounded-full text-[10px] font-semibold uppercase tracking-[0.12em] bg-gray-100 text-gray-500">
                    Results Workspace
                </div>
                <h4 className="font-bold text-xl leading-tight tracking-[-0.01em]" style={{ color: 'var(--accent-text)' }}>Analysis Results</h4>
                <p className="text-sm mt-1" style={{ color: 'var(--accent-text-subtle)' }}>
                    Review findings and export a professional report.
                </p>
            </div>
        </div>

        {reportStatus && (
            <div className={`mt-4 p-4 rounded-xl border flex items-start gap-3 animate-in fade-in slide-in-from-top-2 duration-300 ${reportStatus.type === 'success' ? 'bg-green-50 border-green-100 text-green-800' :
                reportStatus.type === 'warning' ? 'bg-amber-50 border-amber-100 text-amber-800' :
                    'bg-red-50 border-red-100 text-red-800'
                }`}>
                <div className="mt-0.5">
                    {reportStatus.type === 'success' ? <CheckCircle2 className="w-5 h-5 text-green-600" /> :
                        reportStatus.type === 'warning' ? <AlertCircle className="w-5 h-5 text-amber-600" /> :
                            <XCircle className="w-5 h-5 text-red-600" />}
                </div>
                <div className="flex-1 text-left">
                    <div className="font-bold text-sm leading-tight">{reportStatus.title}</div>
                    <div className="text-sm opacity-90 mt-0.5">{reportStatus.message}</div>
                </div>
                <button
                    onClick={() => setReportStatus(null)}
                    className="p-1 hover:bg-black/5 rounded-lg transition-colors"
                >
                    <X className="w-4 h-4 opacity-50" />
                </button>
            </div>
        )}

        <div className="mi-results-actions text-sm self-stretch sm:self-end w-full sm:w-auto flex-wrap ml-0 sm:ml-auto justify-start sm:justify-end mt-4 sm:mt-0">
            <button
                onClick={onNewAnalysis}
                className="mi-new-analysis-btn inline-flex items-center justify-center gap-2 whitespace-nowrap shrink-0 bg-amber-100 text-amber-900 px-3 sm:px-4 py-2 rounded-xl font-semibold text-xs sm:text-sm transition-colors duration-150 border border-amber-300 hover:bg-amber-200 hover:border-amber-400 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
                title="Toggle configuration panel"
            >
                <Settings className="w-4 h-4" />
                New analysis
            </button>
            <button
                onClick={handleSaveReport}
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap shrink-0 bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-xl font-semibold text-xs sm:text-sm transition-colors duration-150 border border-blue-600 hover:bg-blue-700 hover:border-blue-700 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed"
                title="Generate and save PDF report"
            >
                {loading ? (
                    <>
                        <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Generating...
                    </>
                ) : (
                    <>
                        <FileText className="w-4 h-4" />
                        Generate report
                    </>
                )}
            </button>

            <div className="mi-feedback-chip inline-flex items-center gap-1.5 bg-white/95 border border-slate-200 rounded-xl px-2.5 py-1.5 shadow-sm">
                <span className="hidden sm:inline text-slate-500 text-sm font-medium mr-1">Feedback</span>
                <button
                    onClick={async () => {
                        const newFeedback = resultFeedback === 'up' ? null : 'up';
                        setResultFeedback(newFeedback);
                        setShowRandomFeedbackPrompt(false);
                        if (newFeedback) {
                            try {
                                await apiClient.post('/feedback/intelligence', {
                                    tool_id: activeTool.id,
                                    is_positive: true,
                                    query: formData,
                                    response: result.response
                                });
                            } catch (err) {
                                console.error('Failed to submit feedback:', err);
                            }
                        }
                    }}
                    className={`p-1.5 rounded-lg transition-all ${resultFeedback === 'up'
                        ? 'bg-green-50 text-green-600'
                        : 'text-slate-400 hover:bg-slate-100 hover:text-green-600'
                        }`}
                    title="Helpful analysis"
                >
                    <ThumbsUp className="w-4 h-4" />
                </button>
                <button
                    onClick={async () => {
                        const newFeedback = resultFeedback === 'down' ? null : 'down';
                        setResultFeedback(newFeedback);
                        setShowRandomFeedbackPrompt(false);
                        if (newFeedback) {
                            try {
                                await apiClient.post('/feedback/intelligence', {
                                    tool_id: activeTool.id,
                                    is_positive: false,
                                    query: formData,
                                    response: result.response
                                });
                            } catch (err) {
                                console.error('Failed to submit feedback:', err);
                            }
                        }
                    }}
                    className={`p-1.5 rounded-lg transition-all ${resultFeedback === 'down'
                        ? 'bg-red-50 text-red-600'
                        : 'text-slate-400 hover:bg-slate-100 hover:text-red-600'
                        }`}
                    title="Not helpful"
                >
                    <ThumbsDown className="w-4 h-4" />
                </button>
                <button
                    onClick={() => setShowFeedbackModal(true)}
                    className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-blue-600 transition-all"
                    title="Report an issue or leave a comment"
                >
                    <MessageSquare className="w-4 h-4" />
                </button>
            </div>
        </div>
    </div>
));

const VisualizationPanel = React.memo(({ responseText, renderVisualization, visualizationRef }) => {
    const memoResponse = useMemo(() => normalizeMarkdownForDisplay(responseText || ''), [responseText]);
    const visualizationContent = useMemo(() => renderVisualization(), [renderVisualization]);

    return (
        <div className="flex-1 overflow-y-auto max-h-[800px] pr-2 custom-scrollbar">
            <div className="mb-6 prose prose-sm max-w-none p-5 rounded-xl border border-gray-100 shadow-sm shadow-blue-500/5" style={{ background: 'var(--card-bg)', color: 'var(--accent-text)', borderColor: 'var(--card-border)' }}>
                <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                        h1: ({ node, ...props }) => <h1 className="text-xl font-bold mb-4 mt-2 border-b pb-2" style={{ color: 'var(--accent-text)', borderColor: 'var(--card-border)' }} {...props}>{props.children}</h1>,
                        h2: ({ node, ...props }) => <h2 className="text-lg font-bold mb-3 mt-6" style={{ color: 'var(--accent-text)' }} {...props}>{props.children}</h2>,
                        h3: ({ node, ...props }) => <h3 className="text-md font-bold mb-2 mt-4" style={{ color: 'var(--accent-text)' }} {...props}>{props.children}</h3>,
                        ul: ({ node, ...props }) => <ul className="list-disc pl-5 space-y-1 mb-4" {...props} />,
                        li: ({ node, children, ...props }) => {
                            const parsedBar = parseComparisonBarChildren(children);
                            if (parsedBar) {
                                return (
                                    <li className="text-sm leading-relaxed" style={{ color: 'var(--accent-text)' }} {...props}>
                                        <div className="comparison-bar-line">
                                            <span className="comparison-bar-label">{parsedBar.label}:</span>
                                            <span className="comparison-bar-track">
                                                <span className="comparison-bar-fill" style={{ width: `${parsedBar.pctValue}%` }} />
                                                <span className="comparison-bar-text-ghost" aria-hidden="true" />
                                            </span>
                                            <span className="comparison-bar-pct">({parsedBar.pctValue.toFixed(1)}%)</span>
                                        </div>
                                    </li>
                                );
                            }
                            return <li className="text-sm leading-relaxed" style={{ color: 'var(--accent-text)' }} {...props}>{children}</li>;
                        },
                        strong: ({ node, ...props }) => <strong className="font-bold" style={{ color: 'var(--accent-primary)' }} {...props} />,
                        p: ({ node, ...props }) => <p className="mb-4 text-sm leading-relaxed" style={{ color: 'var(--accent-text)' }} {...props} />,
                    }}
                >
                    {memoResponse}
                </ReactMarkdown>
            </div>

            <div className="mb-4 p-2" ref={visualizationRef} style={{ background: 'var(--card-bg)' }}>
                {visualizationContent}
            </div>
        </div>
    );
});

const TOOLS = [
    {
        id: 'pre_failure',
        title: 'Pre-Failure Event Sequencing',
        icon: Activity,
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        description: 'Analyze sequence of events prior to a failure.',
        inputs: [
            { name: 'unit_names', label: 'Truck Unit Search (e.g. TK01)', type: 'text', placeholder: 'TK01, TK02', metadataKey: 'units', icon: Truck },
            { name: 'failure_anchor', label: 'Failure Description (e.g. Engine)', type: 'text', placeholder: 'e.g. Engine Failure', metadataKey: 'failure_types', icon: AlertTriangle },
            { name: 'date', label: 'Failure Date', type: 'date' },
            { name: 'system', label: 'System (Optional)', type: 'text', placeholder: 'e.g. Engine', metadataKey: 'systems', icon: Settings },
            { name: 'window_weeks', label: 'Lookback (Weeks)', type: 'number', defaultValue: 2 }
        ]
    },
    {
        id: 'precursor_pattern',
        title: 'Common Precursor Patterns',
        icon: Layers,
        color: 'text-purple-600',
        bgColor: 'bg-purple-50',
        borderColor: 'border-purple-200',
        description: 'Find events that frequently precede failures.',
        inputs: [
            { name: 'failure_type', label: 'Failure Type', type: 'search', placeholder: 'e.g. Brake or Suspension', metadataKey: 'failure_types', icon: AlertTriangle, defaultValue: 'Brake' },
            { name: 'lookback_window', label: 'Lookback Window (Days)', type: 'number', defaultValue: 14 }
        ]
    },
    {
        id: 'event_count',
        title: 'Fleet Event Counts',
        icon: BarChart3,
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        description: 'Count specific events across the entire fleet.',
        inputs: [
            { name: 'event_type', label: 'Event Type', type: 'text', placeholder: 'Tyre Damage', metadataKey: 'failure_types', icon: Tag },
            { name: 'period', label: 'Period', type: 'text', defaultValue: 'last 6 months' },
            { name: 'search_mode', label: 'Search Mode', type: 'select', options: ['strict', 'relaxed'], defaultValue: 'strict' }
        ]
    },
    {
        id: 'component_analysis',
        title: 'Component Analysis',
        icon: Wrench,
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        description: 'Identify top failing components.',
        inputs: [
            { name: 'count', label: 'Top N Items', type: 'number', defaultValue: 10 },
            { name: 'maintenance_type', label: 'Type', type: 'select', options: ['unplanned', 'planned', 'all'] },
            { name: 'period', label: 'Period', type: 'text', defaultValue: 'last 3 months' }
        ]
    },
    {
        id: 'sla_compliance',
        title: 'SLA Analysis',
        icon: Clock,
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        description: 'Analyze task duration breaches.',
        inputs: [
            { name: 'task_name', label: 'Task Name', type: 'text', placeholder: 'PM Service', metadataKey: 'task_names', icon: Briefcase },
            { name: 'duration_threshold', label: 'Threshold (Hours)', type: 'number', defaultValue: 14 },
            { name: 'period', label: 'Period', type: 'text', defaultValue: 'last 3 months' }
        ]
    },
    {
        id: 'downtime_analysis',
        title: 'Downtime Analysis',
        icon: AlertTriangle,
        color: 'text-amber-600',
        bgColor: 'bg-amber-50',
        borderColor: 'border-amber-200',
        description: 'Analyze downtime drivers and trends.',
        inputs: [
            { name: 'scope', label: 'Scope', type: 'select', options: ['fleet', 'unit'], defaultValue: 'fleet' },
            { name: 'unit_names', label: 'Unit Filter', type: 'text', placeholder: 'TK03, TK05, TK35', hint: 'Comma-separated unit IDs when Scope=unit.' },
            { name: 'event_type', label: 'Event Filter (Optional)', type: 'text', placeholder: 'Electrical Fault', metadataKey: 'failure_types', icon: Filter },
            { name: 'period', label: 'Period', type: 'text', defaultValue: 'last 3 months' },
            { name: 'search_mode', label: 'Search Mode', type: 'select', options: ['strict', 'relaxed'], defaultValue: 'strict' }
        ]
    }
];



// ... existing imports ...

// Helper to generate dynamic colors for component bars based on percentage
const getComponentColor = (percentage) => {
    // Gradient from blue (220) to orange (30) to red (0)
    // 0% -> H:220 (Blue), 100% -> H:0 (Red)
    const hue = Math.max(0, 220 - (percentage * 2.2));
    return {
        background: `hsla(${hue}, 75%, 60%, 0.8)`,
        border: `hsla(${hue}, 80%, 45%, 1)`,
        hover: `hsla(${hue}, 85%, 50%, 1)`
    };
};

const DEFAULT_ANALYSIS_METADATA = {
    units: [],
    failure_types: ['Engine', 'Hydraulic', 'Brakes', 'Electrical', 'Transmission', 'Suspension', 'Cooling', 'Tyres', 'Other'],
    systems: ['Engine', 'Transmission', 'Hydraulic', 'Brake', 'Electrical', 'Tyre', 'Suspension', 'Chassis', 'Cooling', 'Frame'],
    task_names: ['PM Service', 'Inspection', 'Wheel Change', 'Service', 'Repair']
};

const isAzureAppServiceHtml503 = (err) => {
    const status = err?.response?.status;
    const data = err?.response?.data;
    return (
        status === 503 &&
        typeof data === 'string' &&
        (data.includes('diagnostic resources') || data.includes('Application Error'))
    );
};

const MaintenanceIntelligence = ({ uploadToStorage, showHeader = true, apiClient, isAuthenticated = false }) => {
    const { notify } = useNotification();
    const isMobile = useMediaQuery('(max-width: 767px)');
    const [activeTool, setActiveTool] = useState(null);
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(false);
    const [loadingStatus, setLoadingStatus] = useState('');
    const [result, setResult] = useState(null);
    const [reportStatus, setReportStatus] = useState(null);
    const [error, setError] = useState(null);
    const [metadata, setMetadata] = useState({ units: [], failure_types: [], systems: [], task_names: [] });
    const [metadataLoading, setMetadataLoading] = useState(false);
    const [metadataSource, setMetadataSource] = useState('idle');
    const [resultFeedback, setResultFeedback] = useState(null);
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const [feedbackText, setFeedbackText] = useState('');
    const [showRandomFeedbackPrompt, setShowRandomFeedbackPrompt] = useState(false);
    const [isCapturing, setIsCapturing] = useState(false);
    const [isResultFocusMode, setIsResultFocusMode] = useState(false);
    const visualizationRef = React.useRef(null);
    const toolsCarouselRef = React.useRef(null);
    const activePanelsCarouselRef = React.useRef(null);
    const metadataWarnedRef = React.useRef(false);
    const metadataRetryTimerRef = React.useRef(null);
    const [mobileToolIndex, setMobileToolIndex] = useState(0);
    const [mobileActivePanelIndex, setMobileActivePanelIndex] = useState(0);

    const getToolCarouselStep = React.useCallback((container) => {
        if (!container) return 1;
        const firstCard = container.querySelector('[data-carousel-item="tool"]');
        if (!firstCard) return Math.max(container.clientWidth, 1);
        const styles = window.getComputedStyle(container);
        const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
        return Math.max(firstCard.getBoundingClientRect().width + gap, 1);
    }, []);

    const handleToolCarouselScroll = React.useCallback((e) => {
        const container = e.currentTarget;
        const step = getToolCarouselStep(container);
        const idx = Math.round(container.scrollLeft / step);
        setMobileToolIndex(Math.max(0, Math.min(TOOLS.length - 1, idx)));
    }, [getToolCarouselStep]);

    const scrollToTool = React.useCallback((index) => {
        const container = toolsCarouselRef.current;
        if (!container) return;
        const step = getToolCarouselStep(container);
        container.scrollTo({ left: step * index, behavior: 'smooth' });
        setMobileToolIndex(index);
    }, [getToolCarouselStep]);

    const getActivePanelStep = React.useCallback((container) => {
        if (!container) return 1;
        const firstPanel = container.querySelector('[data-carousel-item="active-panel"]');
        if (!firstPanel) return Math.max(container.clientWidth, 1);
        const styles = window.getComputedStyle(container);
        const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
        return Math.max(firstPanel.getBoundingClientRect().width + gap, 1);
    }, []);

    const handleActivePanelsScroll = React.useCallback((e) => {
        const container = e.currentTarget;
        const step = getActivePanelStep(container);
        const idx = Math.round(container.scrollLeft / step);
        const maxIndex = isResultFocusMode ? 0 : 1;
        setMobileActivePanelIndex(Math.max(0, Math.min(maxIndex, idx)));
    }, [getActivePanelStep, isResultFocusMode]);

    const scrollToActivePanel = React.useCallback((index) => {
        const container = activePanelsCarouselRef.current;
        if (!container) return;
        const step = getActivePanelStep(container);
        container.scrollTo({ left: step * index, behavior: 'smooth' });
        setMobileActivePanelIndex(index);
    }, [getActivePanelStep]);

    React.useEffect(() => {
        setMobileActivePanelIndex(0);
        if (activePanelsCarouselRef.current) {
            activePanelsCarouselRef.current.scrollTo({ left: 0, behavior: 'auto' });
        }
    }, [activeTool, isResultFocusMode]);

    // Track usage for random feedback prompts
    const [usageCount, setUsageCount] = React.useState(() => {
        const saved = localStorage.getItem('mi_usage_count');
        return saved ? parseInt(saved, 10) : 0;
    });

    const [lastFeedbackPrompt, setLastFeedbackPrompt] = React.useState(() => {
        const saved = localStorage.getItem('mi_last_feedback_prompt');
        return saved ? parseInt(saved, 10) : 0;
    });

    const fetchMetadata = React.useCallback(async ({ retries = 2 } = {}) => {
        if (metadataRetryTimerRef.current) {
            clearTimeout(metadataRetryTimerRef.current);
            metadataRetryTimerRef.current = null;
        }

        setMetadataLoading(true);
        try {
            for (let attempt = 0; attempt <= retries; attempt++) {
                try {
                    const response = await apiClient.get('/analysis/metadata', { params: { ts: Date.now() } });
                    if (response.data && !response.data.error) {
                        setMetadata({
                            units: response.data.units || [],
                            failure_types: response.data.failure_types || [],
                            systems: response.data.systems || [],
                            task_names: response.data.task_names || []
                        });
                        setMetadataSource('live');
                        metadataWarnedRef.current = false;
                        return true;
                    }
                } catch (err) {
                    const isLastAttempt = attempt === retries;
                    if (isLastAttempt) {
                        const backendUnavailable = isAzureAppServiceHtml503(err);
                        const status = err?.response?.status;
                        const code = err?.code;
                        const message = err?.message || 'Unknown metadata fetch error';

                        console.warn('Failed to fetch analysis metadata:', {
                            status,
                            code,
                            message,
                            backendUnavailable
                        });

                        if (!metadataWarnedRef.current) {
                            notify({
                                type: 'warning',
                                title: 'Metadata unavailable',
                                message: backendUnavailable
                                    ? 'Backend is temporarily unavailable. Using default suggestions and retrying in background.'
                                    : 'Could not load metadata. Using default suggestions.'
                            });
                            metadataWarnedRef.current = true;
                        }

                        if (backendUnavailable && !metadataRetryTimerRef.current) {
                            metadataRetryTimerRef.current = setTimeout(() => {
                                fetchMetadata({ retries: 1 });
                            }, 5000);
                        }
                    } else {
                        await new Promise(resolve => setTimeout(resolve, 400 * (attempt + 1)));
                    }
                }
            }

            setMetadata(prev => ({
                ...prev,
                failure_types: prev.failure_types?.length ? prev.failure_types : DEFAULT_ANALYSIS_METADATA.failure_types,
                systems: prev.systems?.length ? prev.systems : DEFAULT_ANALYSIS_METADATA.systems,
                task_names: prev.task_names?.length ? prev.task_names : DEFAULT_ANALYSIS_METADATA.task_names
            }));
            setMetadataSource('fallback');
            return false;
        } finally {
            setMetadataLoading(false);
        }
    }, [apiClient, notify]);

    React.useEffect(() => {
        return () => {
            if (metadataRetryTimerRef.current) {
                clearTimeout(metadataRetryTimerRef.current);
                metadataRetryTimerRef.current = null;
            }
        };
    }, []);

    // Fetch metadata for suggestions
    React.useEffect(() => {
        if (!isAuthenticated) {
            return;
        }
        fetchMetadata({ retries: 2 });
    }, [fetchMetadata, isAuthenticated]);

    // Random feedback prompt logic (similar to GPT/Copilot)
    const shouldShowFeedbackPrompt = React.useCallback(() => {
        const now = Date.now();
        const timeSinceLastPrompt = now - lastFeedbackPrompt;
        const thirtyMinutes = 30 * 60 * 1000; // Reduced cooldown to 30 minutes

        // Don't prompt if:
        // 1. Less than 30 minutes since last prompt (cooldown)
        // 2. User has already given feedback for this result
        if (timeSinceLastPrompt < thirtyMinutes || resultFeedback) {
            return false;
        }

        // Show prompt based on usage patterns (INCREASED PROBABILITIES):
        // - 60% chance after 2nd use
        // - 85% chance after 3rd use
        // - 100% chance after 5th use and every 3 uses thereafter
        const sessionCount = usageCount % 100; // Reset every 100 to avoid localStorage bloat

        if (sessionCount === 2 && Math.random() < 0.6) return true;
        if (sessionCount === 3 && Math.random() < 0.85) return true;
        if (sessionCount >= 5 && sessionCount % 3 === 0) return true; // 100% chance, every 3 uses

        return false;
    }, [lastFeedbackPrompt, resultFeedback, usageCount]);


    const handleFeedbackSubmit = async () => {
        try {
            await apiClient.post('/feedback/intelligence', {
                tool_id: activeTool.id,
                is_positive: resultFeedback === 'up',
                query: formData,
                response: result?.response || '',
                comment: feedbackText
            });
            setShowFeedbackModal(false);
            setFeedbackText('');
            // Optional: Show success toast
        } catch (err) {
            console.error('Failed to submit feedback:', err);
        }
    };

    const handleToolClick = (tool) => {
        setActiveTool(tool);
        setFormData({});
        setResult(null);
        setIsResultFocusMode(false);
        setError(null);
        setLoadingStatus('');
        setResultFeedback(null);

        // Set default values
        const defaults = {};
        tool.inputs.forEach(input => {
            if (input.defaultValue) defaults[input.name] = input.defaultValue;
        });
        setFormData(defaults);

        const needsMetadata = tool.inputs.some(input =>
            input.metadataKey && (!metadata[input.metadataKey] || metadata[input.metadataKey].length === 0)
        );
        if (needsMetadata) {
            fetchMetadata({ retries: 1 });
        }
    };

    const handleInputChange = (e) => {
        // ...
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const buildPayload = (tool, data) => {
        const payload = { ...data };

        // Map search mode to strict_search boolean
        if (payload.search_mode) {
            payload.strict_search = payload.search_mode === 'strict';
            delete payload.search_mode;
        }

        // Special handling for pre_failure - rename 'date' to 'failure_date' for backend
        if (tool.id === 'pre_failure' && payload.date) {
            payload.failure_date = payload.date;
            delete payload.date;
        }

        // Convert unit_names string to array if present
        if (payload.unit_names && typeof payload.unit_names === 'string') {
            payload.unit_names = payload.unit_names.split(',').map(s => s.trim()).filter(Boolean);
        }

        // Scope-aware payload for downtime analysis.
        if (tool.id === 'downtime_analysis') {
            const scope = payload.scope || 'fleet';
            if (scope === 'fleet') {
                delete payload.unit_names;
            }
            // Downtime UI no longer exposes top-units count filter.
            delete payload.count;
            delete payload.scope;
        }

        return payload;
    };

    const handleRunAnalysis = async () => {
        setLoading(true);
        setResult(null);
        setError(null);
        setResultFeedback(null);
        setLoadingStatus('Preparing analysis...');

        try {
            // Validate inputs
            const tool = activeTool;
            const payload = buildPayload(tool, formData);

            if (tool.id === 'downtime_analysis' && (formData.scope || 'fleet') === 'unit') {
                const units = payload.unit_names || [];
                if (!Array.isArray(units) || units.length === 0) {
                    throw new Error('Please enter at least one unit ID (e.g., TK03, TK05, TK35).');
                }
            }

            setLoadingStatus('Querying database...');

            // Use apiClient for authenticated request
            const response = await apiClient.post(`/analysis/${tool.id}`, payload);
            const data = response.data;

            setLoadingStatus('Processing results...');

            if (!data.success) {
                throw new Error(data.response || 'Analysis failed');
            }
            setResult(data);
            setIsResultFocusMode(true);

            // Track usage and potentially show feedback prompt
            const newUsageCount = usageCount + 1;
            setUsageCount(newUsageCount);
            localStorage.setItem('mi_usage_count', newUsageCount.toString());

            // Check if we should show the random feedback prompt
            // Small delay to let user see the results first
            setTimeout(() => {
                if (shouldShowFeedbackPrompt()) {
                    setShowRandomFeedbackPrompt(true);
                    setLastFeedbackPrompt(Date.now());
                    localStorage.setItem('mi_last_feedback_prompt', Date.now().toString());
                }
            }, 3000); // Wait 3 seconds after results load

        } catch (err) {
            console.error(err);
            setError(err.message || 'An error occurred during analysis');
            setIsResultFocusMode(false);
        } finally {
            setLoading(false);
            setLoadingStatus('');
        }
    };

    const handleExportToExcel = useCallback(async (toolId, payload) => {
        try {
            setLoading(true);
            setLoadingStatus('Generating Excel report...');

            const tool = TOOLS.find(t => t.id === toolId);
            const finalPayload = tool ? buildPayload(tool, payload) : payload;

            // Use apiClient with blob response type
            const response = await apiClient.post(`/analysis/${toolId}/export`, finalPayload, {
                responseType: 'blob'
            });

            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `maintenance_analysis_${toolId}_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

        } catch (err) {
            console.error(err);
            const errorMsg = err?.response?.data?.detail || err?.message || 'Unknown error';
            notify({
                type: 'error',
                title: 'Excel export failed',
                message: errorMsg
            });
        } finally {
            setLoading(false);
            setLoadingStatus('');
        }
    }, [apiClient, setLoading, setLoadingStatus, notify]);

    const handleSaveReport = async () => {
        if (!result) return;

        try {
            setLoading(true);
            setLoadingStatus('Generating AI summary for report...');

            // 1. Call AI to generate professional summary
            let aiSummary = result.response || 'Professional analysis summary not generated.';

            try {
                const summaryResponse = await apiClient.post('/generate-report-summary', {
                    tool_type: activeTool.id,
                    metadata: result.metadata || {},
                    response: result.response || ''
                });

                if (summaryResponse.data && summaryResponse.data.summary) {
                    aiSummary = summaryResponse.data.summary;
                } else {
                    console.warn('AI summary empty, using fallback');
                }
            } catch (aiErr) {
                console.error('AI summary request failed:', aiErr);
                const errorMsg = aiErr.response?.data?.detail || aiErr.message || 'Unknown error';

                if (aiErr.response?.status === 401) {
                    aiSummary = `### Authentication Expired\n\nYour session has expired. Please refresh the page or log in again to generate an AI summary.\n\n**Original Analysis:**\n\n${result.response}`;
                } else {
                    aiSummary = `### AI Summary Generation Failed\n\n**Error:** ${errorMsg}\n\n**Original Analysis:**\n\n${result.response}`;
                }
            }

            setLoadingStatus('Creating PDF document...');

            // 2. Create PDF with professional layout
            const doc = new jsPDF();
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin = 20;
            let yPos = 20;
            const maxPages = 50;

            // Helper for page breaks
            const checkNewPage = (neededSpace) => {
                if (yPos + neededSpace > pageHeight - margin) {
                    if (doc.internal.getNumberOfPages() >= maxPages) {
                        return false;
                    }
                    doc.addPage();
                    yPos = margin + 10;
                    return true;
                }
                return false;
            };

            // 3. Header
            const toolColor = activeTool.color.includes('red') ? [220, 38, 38] :
                activeTool.color.includes('blue') ? [37, 99, 235] :
                    activeTool.color.includes('green') ? [22, 163, 74] :
                        activeTool.color.includes('orange') ? [234, 88, 12] :
                            activeTool.color.includes('purple') ? [147, 51, 234] : [75, 85, 99];

            doc.setFillColor(...toolColor);
            doc.rect(0, 0, pageWidth, 40, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(20);
            doc.setFont('helvetica', 'bold');
            doc.text(activeTool.title || 'Analysis Report', margin, 22);

            doc.setFontSize(11);
            doc.setFont('helvetica', 'normal');
            doc.text(`Generated: ${new Date().toLocaleString('en-AU', { timeZone: 'Australia/Brisbane', hour12: true })}`, margin, 33);

            yPos = 55;
            doc.setTextColor(0, 0, 0);

            // 4. Key Metrics Box (condensed)
            if (result.metadata) {
                doc.setFillColor(248, 250, 252); // Light gray background
                doc.rect(margin - 5, yPos - 5, pageWidth - (margin * 2) + 10, 40, 'F');

                doc.setFontSize(12);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(55, 65, 81);
                doc.text('Key Metrics', margin, yPos + 3);
                yPos += 12;

                doc.setFontSize(9);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(75, 85, 99);

                // Show only most important metrics
                const keyMetrics = [
                    'failure_type', 'event_type', 'system', 'task_name',
                    'total_downtime', 'total_events', 'incident_count', 'avg_downtime',
                    'breach_count', 'breach_rate_pct', 'scanned_records', 'scanned_count',
                    'analyzed_events', 'lookback_window_days', 'total_matches',
                    'prediction_count', 'accuracy_pct', 'analyzed_failures',
                    'total_issues', 'event_count', 'total_records', 'context'
                ];

                // One-level flattening for common nested metadata containers
                const flatMetadata = { ...result.metadata };
                ['downtime_data', 'impact_metadata', 'delay_data', 'summary', 'viz_config'].forEach(container => {
                    if (result.metadata[container] && typeof result.metadata[container] === 'object') {
                        Object.entries(result.metadata[container]).forEach(([k, v]) => {
                            if (v !== null && v !== undefined && (typeof v === 'number' || typeof v === 'string')) {
                                if (!flatMetadata[k]) flatMetadata[k] = v;
                            }
                        });
                    }
                });

                let metricCount = 0;

                Object.entries(flatMetadata).forEach(([key, value]) => {
                    if (keyMetrics.includes(key) && value !== null && value !== undefined && metricCount < 6) {
                        const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                        let valueStr = typeof value === 'number' ? value.toLocaleString() : String(value);
                        if (key.includes('downtime') && typeof value === 'number') {
                            valueStr = `${value.toFixed(1)} hrs`;
                        } else if (key.includes('pct') || key.includes('rate')) {
                            // Only add % if it's not already a string with %
                            if (typeof value === 'number') valueStr = `${value}%`;
                            else if (typeof value === 'string' && !value.includes('%')) valueStr = `${value}%`;
                        }

                        doc.text(`${label}: ${valueStr}`, margin + (metricCount % 2 === 0 ? 0 : 80), yPos);
                        if (metricCount % 2 === 1) yPos += 7;
                        metricCount++;
                    }
                });

                yPos += 20;
            }

            // 5. AI-Generated Summary Section
            checkNewPage(20);
            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(30, 41, 59);
            doc.text('Executive Summary', margin, yPos);
            yPos += 12;

            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(55, 65, 81);

            // Sanitize text to avoid PDF rendering glitches
            const safeSummary = (aiSummary || 'No content available')
                .replace(/&/g, 'and')  // Replace ampersands which caused glitches
                .replace(/[^\x20-\x7E\n\r]/g, ''); // Remove non-printable ascii

            // Split by lines to handle formatting line-by-line
            const rawLines = safeSummary.split(/\r?\n/);

            let lineCount = 0;

            for (let i = 0; i < rawLines.length; i++) {
                let line = rawLines[i].trim();
                if (!line) {
                    yPos += 3; // Small gap for empty lines
                    continue;
                }

                // Detect marked Headers (###, **, or I. II.)
                const isHeader = line.startsWith('#') ||
                    line.match(/^[IVX]+\.\s/) ||
                    line.match(/^\d+\.\s/) ||
                    (line.startsWith('**') && line.endsWith('**'));

                // Clean markdown artifacts
                let cleanLine = line.replace(/^#+\s*/, '')
                    .replace(/\*\*/g, '')
                    .replace(/_/g, '');

                if (isHeader) {
                    yPos += 4; // Extra space before header
                    checkNewPage(15);
                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(11);
                    doc.setTextColor(30, 41, 59);

                    // Add subtle underline for main sections
                    if (line.match(/^[IVX]+\.\s/) || line.includes('EXECUTIVE REPORT')) {
                        doc.line(margin, yPos + 1, margin + doc.getTextWidth(cleanLine), yPos + 1);
                    }
                } else {
                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(10);
                    doc.setTextColor(55, 65, 81);
                }

                const splitLines = doc.splitTextToSize(cleanLine, pageWidth - (margin * 2));

                for (const splitLine of splitLines) {
                    if (lineCount > 5000) break;
                    checkNewPage(6);
                    doc.text(splitLine, margin, yPos);
                    yPos += 6; // Line height
                    lineCount++;
                }

                // Reset font for next iteration if needed
                if (isHeader) {
                    yPos += 2; // Space after header
                }
            }

            // 5.5 Capture Visualizations
            if (visualizationRef.current) {
                try {
                    setLoadingStatus('Capturing charts...');

                    // Force switch to charts view
                    setIsCapturing(true);
                    // Allow UI to update
                    await new Promise(resolve => setTimeout(resolve, 800)); // Increased delay for chart rendering

                    // Add a new page for visuals
                    doc.addPage();
                    doc.setFontSize(16);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(30, 41, 59);
                    doc.text('Data Visualization', margin, 20);

                    const canvas = await html2canvas(visualizationRef.current, {
                        scale: 2, // Retain high quality
                        useCORS: true,
                        logging: false,
                        backgroundColor: '#ffffff'
                    });

                    const imgData = canvas.toDataURL('image/png');
                    const imgWidth = pageWidth - (margin * 2);
                    const imgHeight = (canvas.height * imgWidth) / canvas.width;

                    // If image is too tall for one page, we might need to handle it, 
                    // but usually charts fit. Truncate/scale if massive.
                    const maxImgHeight = pageHeight - 40;

                    if (imgHeight > maxImgHeight) {
                        // Fit to page height if too tall
                        const ratio = maxImgHeight / imgHeight;
                        doc.addImage(imgData, 'PNG', margin, 30, imgWidth * ratio, maxImgHeight);
                    } else {
                        doc.addImage(imgData, 'PNG', margin, 30, imgWidth, imgHeight);
                    }

                } catch (vizErr) {
                    console.error('Failed to capture visualization:', vizErr);
                    // Don't fail the whole report, just log and continue
                } finally {
                    setIsCapturing(false); // Revert view
                }
            }

            // 6. Footer
            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setTextColor(150);
                doc.text(
                    `Page ${i} of ${pageCount} | ${activeTool.title} | Maintenance Intelligence`,
                    pageWidth / 2,
                    pageHeight - 10,
                    { align: 'center' }
                );
            }

            // 7. Upload
            const blob = doc.output('blob');
            const cleanTitle = (activeTool?.title || 'General').replace(/[^a-zA-Z0-9\s]/g, '').trim();
            const folderName = `Reports/${cleanTitle}`;

            const reportFileName = `${activeTool?.id || 'analysis'}_${new Date().getTime()}.pdf`;
            const file = new File([blob], reportFileName, { type: 'application/pdf' });

            setLoadingStatus(`Uploading to ${cleanTitle}...`);
            try {
                if (typeof uploadToStorage !== 'function') {
                    throw new Error('Storage API is not available');
                }

                await uploadToStorage(file, folderName);
                const status = {
                    type: 'success',
                    title: 'Report saved',
                    message: `Saved to "Reports > ${cleanTitle}". Go to Storage Panel to see the file.`
                };
                setReportStatus(status);
                notify(status);
                // Auto-clear after 10 seconds
                setTimeout(() => setReportStatus(null), 10000);
            } catch (uploadErr) {
                console.error('Report upload failed, downloading locally instead:', uploadErr);
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = reportFileName;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);

                const uploadErrorMsg = uploadErr?.response?.data?.detail || uploadErr?.message || 'Unknown upload error';
                const status = {
                    type: 'warning',
                    title: 'Saved locally',
                    message: `Storage upload failed (${uploadErrorMsg}). Downloaded the PDF to your device instead.`
                };
                setReportStatus(status);
                notify(status);
                setTimeout(() => setReportStatus(null), 10000);
            }

        } catch (err) {
            console.error('Failed to save PDF:', err);
            const errorMsg = err?.response?.data?.detail || err?.message || 'Unknown error';
            const status = {
                type: 'error',
                title: 'Report generation failed',
                message: errorMsg
            };
            setReportStatus(status);
            notify(status);
            setTimeout(() => setReportStatus(null), 10000);
        }
        finally {
            setLoading(false);
            setLoadingStatus('');
        }
    };

    // Render Helpers
    const renderVisualization = useCallback(() => {
        if (!result || !result.metadata) return null;

        const { metadata } = result;

        // SLA Specific Header (Breach Stats) - Render this alongside detailed view if available
        let slaSummary = null;
        if ((activeTool.id === 'sla_compliance' || metadata.handler_type === 'sla_compliance') && metadata.percentiles) {

            // Safe access to top offenders
            const offenders = metadata.top_offenders || {};
            const offendersList = Object.entries(offenders).map(([u, c]) => `${u} (${c})`).join(', ');

            slaSummary = (
                <div className="grid grid-cols-3 gap-4 mb-6 text-center animate-in fade-in slide-in-from-bottom-2">
                    <div className="p-4 bg-red-50 rounded-xl border border-red-100 shadow-sm">
                        <div className="text-xs font-semibold text-red-600 uppercase tracking-wider mb-1">Total Breaches</div>
                        <div className="text-2xl font-bold text-red-700">{metadata.breach_count}</div>
                        <div className="text-xs text-red-500 mt-1 font-medium">{metadata.breach_rate_pct}% of events</div>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-xl border border-blue-100 shadow-sm">
                        <div className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-1">Median Duration</div>
                        <div className="text-2xl font-bold text-blue-700">{metadata.percentiles.p50}h</div>
                        <div className="text-xs text-blue-500 mt-1 font-medium">Standard Performance</div>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-xl border border-amber-100 shadow-sm">
                        <div className="text-xs font-semibold text-amber-600 uppercase tracking-wider mb-1">P90 Duration</div>
                        <div className="text-2xl font-bold text-amber-700">{metadata.percentiles.p90}h</div>
                        <div className="text-xs text-amber-500 mt-1 font-medium">Outlier Threshold</div>
                    </div>
                    {offendersList && (
                        <div className="col-span-3 mt-1 p-3 bg-gray-50 rounded-lg border border-gray-100 text-sm text-left flex items-start">
                            <AlertTriangle className="w-4 h-4 text-orange-500 mr-2 mt-0.5 flex-shrink-0" />
                            <div>
                                <strong className="text-gray-700">Top Offenders (Most Breaches):</strong>
                                <span className="text-gray-600 ml-1">{offendersList}</span>
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        // Downtime Analysis - Custom Charts
        let downtimeView = null;
        if (activeTool.id === 'downtime_analysis' && metadata.downtime_data) {
            const elements = [];

            // Trend Chart
            if (metadata.downtime_data.monthly_trend) {
                const trend = metadata.downtime_data.monthly_trend;
                const trendData = {
                    labels: Object.keys(trend),
                    datasets: [{
                        label: 'Downtime Hours',
                        data: Object.values(trend),
                        borderColor: 'rgb(217, 119, 6)',
                        backgroundColor: 'rgba(217, 119, 6, 0.1)',
                        borderWidth: 3,
                        pointBackgroundColor: '#fff',
                        pointBorderColor: 'rgb(217, 119, 6)',
                        pointBorderWidth: 2,
                        pointRadius: 4,
                        pointHoverRadius: 7,
                        pointHoverBackgroundColor: 'rgb(217, 119, 6)',
                        pointHoverBorderColor: '#fff',
                        pointHoverBorderWidth: 2,
                        tension: 0.4,
                        fill: true
                    }]
                };
                elements.push(
                    <div key="trend" className="h-96 mt-4 p-5 rounded-xl border border-gray-100 shadow-sm" style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>
                        <h5 className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: 'var(--accent-text-subtle)' }}>Monthly Downtime Trend</h5>
                        <Line
                            data={trendData}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: { display: false },
                                    tooltip: {
                                        backgroundColor: 'rgba(17, 24, 39, 0.9)',
                                        padding: 12,
                                        titleFont: { size: 14, weight: 'bold' },
                                        bodyFont: { size: 13 },
                                        cornerRadius: 8,
                                        displayColors: false
                                    }
                                },
                                scales: {
                                    y: {
                                        beginAtZero: true,
                                        grid: { color: 'rgba(0, 0, 0, 0.03)' },
                                        ticks: { font: { size: 11 } }
                                    },
                                    x: {
                                        grid: { display: false },
                                        ticks: { font: { size: 11 } }
                                    }
                                }
                            }}
                        />
                    </div>
                );
            }

            // Top Units / Drivers Chart
            const topDrivers = metadata.downtime_data.top_units || metadata.downtime_data.top_drivers;
            if (topDrivers && topDrivers.length > 0) {
                const drivers = topDrivers.slice(0, 10);
                const data = {
                    labels: drivers.map(d => d.unit),
                    datasets: [{
                        label: 'Total Downtime Hours',
                        data: drivers.map(d => d.total_hours),
                        backgroundColor: 'rgba(217, 119, 6, 0.8)',
                        hoverBackgroundColor: 'rgba(217, 119, 6, 1)',
                        borderRadius: 6,
                        borderWidth: 0
                    }]
                };
                elements.push(
                    <div key="drivers" className="h-[30rem] mt-4 p-5 rounded-xl border border-gray-100 shadow-sm" style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>
                        <h5 className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: 'var(--accent-text-subtle)' }}>Top 10 Units by Downtime</h5>
                        <div className="h-[calc(100%-2rem)] pb-2">
                            <Bar
                                data={data}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    layout: {
                                        padding: {
                                            bottom: 12
                                        }
                                    },
                                    plugins: {
                                        legend: { display: false },
                                        tooltip: {
                                            backgroundColor: 'rgba(17, 24, 39, 0.9)',
                                            cornerRadius: 8,
                                            padding: 12,
                                            titleFont: { size: 13, weight: 'bold' },
                                            bodyFont: { size: 12 },
                                            displayColors: false,
                                            callbacks: {
                                                title: (context) => {
                                                    const unit = context[0].label;
                                                    return unit;
                                                },
                                                label: (context) => {
                                                    const idx = context.dataIndex;
                                                    const driver = drivers[idx];
                                                    return `Total downtime hours: ${driver.total_hours}`;
                                                },
                                                afterLabel: (context) => {
                                                    const idx = context.dataIndex;
                                                    const driver = drivers[idx];
                                                    if (driver.event_number) {
                                                        return `Events by truck: ${driver.event_number}`;
                                                    }
                                                    return '';
                                                }
                                            }
                                        }
                                    },
                                    scales: {
                                        y: {
                                            beginAtZero: true,
                                            grid: { color: 'rgba(0, 0, 0, 0.03)' }
                                        },
                                        x: {
                                            grid: { display: false },
                                            ticks: {
                                                padding: 8
                                            }
                                        }
                                    }
                                }}
                            />
                        </div>
                    </div>
                );
            }

            if (elements.length > 0) {
                downtimeView = <div className="space-y-4">{elements}</div>;
            }
        }

        // Component Analysis - Custom Chart
        let componentView = null;
        if (activeTool.id === 'component_analysis' && metadata.component_data) {
            const topN = metadata.component_data;
            const topNLabel = metadata.top_n || topN.length || 10;

            const chartData = {
                labels: topN.map(c => `${c.code} - ${c.description}`),
                datasets: [{
                    label: 'Event Count',
                    data: topN.map(c => c.count),
                    backgroundColor: topN.map(c => getComponentColor(c.percentage).background),
                    borderColor: topN.map(c => getComponentColor(c.percentage).border),
                    hoverBackgroundColor: topN.map(c => getComponentColor(c.percentage).hover),
                    borderWidth: 1,
                    borderRadius: 8,
                    borderSkipped: false,
                    barThickness: 32
                }]
            };

            const chartOptions = {
                indexAxis: 'y',
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        backgroundColor: 'rgba(17, 24, 39, 0.9)',
                        titleFont: { size: 13, weight: 'bold' },
                        bodyFont: { size: 12 },
                        padding: 12,
                        cornerRadius: 10,
                        callbacks: {
                            label: (context) => {
                                const item = topN[context.dataIndex];
                                return [
                                    `Count: ${item.count} events`,
                                    `Share: ${item.percentage}% of total`
                                ];
                            }
                        }
                    }
                },
                scales: {
                    x: {
                        beginAtZero: true,
                        grid: { color: 'rgba(0, 0, 0, 0.04)' },
                        ticks: { font: { size: 11 } }
                    },
                    y: {
                        grid: { display: false },
                        ticks: {
                            font: { size: 11, weight: '500' },
                            color: '#374151'
                        }
                    }
                },
                animation: {
                    duration: 1500,
                    easing: 'easeOutQuart'
                }
            };

            componentView = (
                <div className="mt-6 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <div className="flex items-center justify-between mb-6">
                        <h5 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Top {topNLabel} Components by Event Frequency</h5>
                    </div>
                    <div className="h-[450px]">
                        <Bar data={chartData} options={chartOptions} />
                    </div>
                </div>
            );
        }

        // Universal Visualization (Table + Charts + Custom)
        let mainViz = null;
        if (metadata.handler_type || metadata.events) {
            const customCharts = componentView || downtimeView;
            const prefersCharts = Boolean(customCharts);
            mainViz = (
                <DataVisualization
                    metadata={metadata}
                    onExport={() => handleExportToExcel(activeTool.id, formData)}
                    customCharts={customCharts}
                    defaultView={prefersCharts ? 'charts' : 'table'}
                    forcedView={isCapturing ? 'charts' : null}
                />
            );
        }

        // Return combined view
        if (slaSummary || mainViz) {
            return (
                <div className="space-y-6">
                    {slaSummary}
                    {mainViz}
                </div>
            );
        }

        // --- LEGACY RENDERERS (Fallback for old handlers) ---

        // 1. Precursor Pattern Chart
        if (activeTool.id === 'precursor_pattern' && metadata.precursors) {
            const data = {
                labels: metadata.precursors.map(p => p.event),
                datasets: [{
                    label: 'Frequency (%)',
                    data: metadata.precursors.map(p => p.frequency_pct),
                    backgroundColor: 'rgba(147, 51, 234, 0.6)',
                    borderColor: 'rgb(147, 51, 234)',
                    borderWidth: 1
                }]
            };
            return (
                <div className="h-64 mt-4">
                    <Bar data={data} options={{ responsive: true, plugins: { legend: { position: 'top' } } }} />
                </div>
            );
        }

        // 2. Event Count Chart
        if (activeTool.id === 'event_count' && metadata.breakdown) {
            const data = {
                labels: Object.keys(metadata.breakdown),
                datasets: [{
                    label: 'Event Count',
                    data: Object.values(metadata.breakdown),
                    backgroundColor: 'rgba(34, 197, 94, 0.6)',
                    borderColor: 'rgb(34, 197, 94)',
                }]
            };
            return (
                <div className="h-64 mt-4">
                    <Bar data={data} options={{ responsive: true }} />
                    {metadata.outliers && metadata.outliers.length > 0 && (
                        <div className="mt-2 p-2 bg-red-50 text-red-700 text-sm rounded border border-red-200 flex items-center">
                            <AlertTriangle className="w-4 h-4 mr-2" />
                            Wait! Outliers detected: {metadata.outliers.join(', ')}
                        </div>
                    )}
                </div>
            );
        }

        // 5. Downtime Analysis - Trend & Drivers (Moved to top)
        // 5b. Downtime Impact (Merged)

        // 6. Pre-Failure Timeline
        if (activeTool.id === 'pre_failure' && metadata.events) {
            return (
                <div className="mt-4 border-l-2 border-blue-200 ml-2 pl-4 space-y-4 max-h-80 overflow-y-auto">
                    {metadata.events.map((evt, i) => (
                        <div key={i} className="relative">
                            <div className="absolute -left-[21px] top-1 w-3 h-3 bg-blue-500 rounded-full border-2 border-white"></div>
                            <div className="text-xs text-gray-500">
                                {evt.timestamp ? new Date(evt.timestamp).toLocaleString() : (evt.formatted_date || 'Unknown Date')}
                            </div>
                            <div className="text-sm font-medium">{evt.description}</div>
                            {evt.work_order !== 'N/A' && <div className="text-xs text-gray-400">WO: {evt.work_order}</div>}
                        </div>
                    ))}
                </div>
            );
        }

        return null;
    }, [activeTool, result, formData, handleExportToExcel, isCapturing]);

    return (
        <div
            className={showHeader && !isMobile ? 'card' : ''}
            style={showHeader && !isMobile ? { borderRadius: 'var(--radius-xl)', overflow: 'hidden' } : { overflow: 'visible' }}
            data-tour-id="mi-section"
        >
            {showHeader && (
                <div className="p-6 bg-gradient-to-r backdrop-blur-sm" style={{ borderBottom: '1px solid var(--card-border)', background: 'linear-gradient(90deg, var(--bg-tertiary) 0%, var(--card-bg) 100%)' }}>
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-xl font-bold flex items-center" style={{ color: 'var(--accent-text)' }}>
                                <div className="p-2.5 rounded-lg mr-3" style={{ background: 'var(--status-info-bg)' }}>
                                    <Activity className="h-5 w-5" style={{ color: 'var(--status-info)' }} />
                                </div>
                                Maintenance Intelligence
                            </h3>
                            <p className="text-sm mt-1 ml-12" style={{ color: 'var(--accent-text-subtle)' }}>
                                AI-driven pattern recognition and predictive analytics
                            </p>
                        </div>
                        {activeTool && (
                            <button
                                onClick={() => setActiveTool(null)}
                                className="text-sm font-medium flex items-center px-3 py-1.5 rounded-lg border shadow-sm hover:bg-gray-50 transition-all"
                                style={{
                                    color: 'var(--accent-text-subtle)',
                                    borderColor: 'var(--card-border)',
                                    background: 'var(--card-bg)'
                                }}
                            >
                                <ArrowRight className="h-4 w-4 mr-1.5 rotate-180" />
                                Back to Tools
                            </button>
                        )}
                    </div>
                </div>
            )}

            <div
                className={showHeader ? (activeTool ? 'p-4 sm:p-6 min-h-[500px]' : 'p-4 sm:p-6') : (activeTool ? 'min-h-[500px]' : '')}
                style={{ background: (showHeader && !isMobile) ? 'var(--bg-tertiary)' : 'transparent' }}
            >
                {!showHeader && activeTool && (
                    <div className="flex justify-end mb-4">
                        <button
                            onClick={() => setActiveTool(null)}
                            className="text-sm font-medium flex items-center px-3 py-1.5 rounded-lg border shadow-sm hover:bg-gray-50 transition-all"
                            style={{
                                color: 'var(--accent-text-subtle)',
                                borderColor: 'var(--card-border)',
                                background: 'var(--card-bg)'
                            }}
                        >
                            <ArrowRight className="h-4 w-4 mr-1.5 rotate-180" />
                            Back to Tools
                        </button>
                    </div>
                )}
                {!activeTool ? (
                    <>
                        <div
                            ref={toolsCarouselRef}
                            onScroll={handleToolCarouselScroll}
                            className="flex md:grid md:grid-cols-2 lg:grid-cols-3 gap-5 overflow-x-auto md:overflow-visible snap-x snap-mandatory md:snap-none scrollbar-hide -mx-1 px-1 pb-2"
                        >
                            {TOOLS.map((tool, index) => (
                                <button
                                    key={tool.id}
                                    onClick={() => handleToolClick(tool)}
                                    data-carousel-item="tool"
                                    className="group relative flex flex-col items-start p-6 rounded-xl border bg-white hover:-translate-y-1 transition-all duration-300 text-left w-full hover-card min-w-[280px] sm:min-w-[340px] md:min-w-0 shrink-0 snap-start"
                                    style={{
                                        borderColor: 'var(--card-border)',
                                        animationDelay: `${index * 50}ms`
                                    }}
                                >
                                    <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${tool.bgColor} opacity-0 group-hover:opacity-20 rounded-bl-full transition-opacity duration-300 pointer-events-none`} />

                                    <div className={`p-3 rounded-xl mb-4 ${tool.bgColor} ${tool.color} group-hover:scale-110 transition-transform duration-300`}>
                                        <tool.icon className="h-6 w-6" />
                                    </div>

                                    <h4 className="font-bold mb-2 text-base transition-colors" style={{ color: 'var(--accent-text)' }}>
                                        {tool.title}
                                    </h4>
                                    <p className="text-sm leading-relaxed" style={{ color: 'var(--accent-text-subtle)' }}>
                                        {tool.description}
                                    </p>

                                    <div className="mt-4 flex items-center text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity transform translate-y-2 group-hover:translate-y-0" style={{ color: 'var(--accent-primary)' }}>
                                        Launch Tool <ArrowRight className="h-3 w-3 ml-1" />
                                    </div>
                                </button>
                            ))}
                        </div>
                        <div className="flex md:hidden items-center justify-center gap-2 mt-2 mb-1">
                            {TOOLS.map((tool, idx) => (
                                <button
                                    key={`tool-dot-${tool.id}`}
                                    type="button"
                                    onClick={() => scrollToTool(idx)}
                                    aria-label={`Go to tool ${idx + 1}`}
                                    className={`h-2 rounded-full transition-all ${mobileToolIndex === idx ? 'w-5 bg-blue-500' : 'w-2 bg-gray-300'}`}
                                />
                            ))}
                        </div>
                    </>
                ) : (
                    <>
                        <div className="lg:hidden animate-in fade-in slide-in-from-bottom-4 duration-300">
                            <div
                                ref={activePanelsCarouselRef}
                                onScroll={handleActivePanelsScroll}
                                className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide -mx-1 px-1 pb-2"
                            >
                                {!isResultFocusMode && (
                                    <div data-carousel-item="active-panel" className="w-full min-w-full max-w-full snap-start shrink-0">
                                        <ToolConfigPanel
                                            activeTool={activeTool}
                                            formData={formData}
                                            metadata={metadata}
                                            metadataLoading={metadataLoading}
                                            metadataSource={metadataSource}
                                            loading={loading}
                                            handleInputChange={handleInputChange}
                                            setFormData={setFormData}
                                            handleRunAnalysis={handleRunAnalysis}
                                        />
                                    </div>
                                )}
                                <div data-carousel-item="active-panel" className="w-full min-w-full max-w-full snap-start shrink-0">
                                    <div className="w-full">
                                        <div className="relative h-full border-0 rounded-none shadow-none sm:border sm:border-gray-200 sm:rounded-2xl sm:shadow-sm overflow-hidden flex flex-col min-h-[560px]" style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>
                                            {error && (
                                                <div className="m-6 p-4 bg-red-50 border border-red-100 text-red-700 rounded-lg flex items-start animate-in fade-in slide-in-from-top-2">
                                                    <AlertTriangle className="h-5 w-5 mr-3 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <h4 className="font-semibold text-sm">Analysis Failed</h4>
                                                        <p className="text-sm opacity-90">{error}</p>
                                                    </div>
                                                </div>
                                            )}

                                            {result ? (
                                                <div className="flex-1 flex flex-col p-6 animate-in fade-in duration-500">
                                                    <ResultsHeaderToolbar
                                                        result={result}
                                                        error={error}
                                                        handleSaveReport={handleSaveReport}
                                                        reportStatus={reportStatus}
                                                        setReportStatus={setReportStatus}
                                                        onNewAnalysis={() => setIsResultFocusMode(prev => !prev)}
                                                        loading={loading}
                                                        resultFeedback={resultFeedback}
                                                        setResultFeedback={setResultFeedback}
                                                        setShowRandomFeedbackPrompt={setShowRandomFeedbackPrompt}
                                                        setShowFeedbackModal={setShowFeedbackModal}
                                                        apiClient={apiClient}
                                                        activeTool={activeTool}
                                                        formData={formData}
                                                    />
                                                    {loading && loadingStatus && (
                                                        <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50/80 px-4 py-3 flex items-center gap-3 animate-in fade-in">
                                                            <div className="w-4 h-4 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin" />
                                                            <div className="text-sm font-medium text-blue-800">{loadingStatus}</div>
                                                        </div>
                                                    )}
                                                    <VisualizationPanel
                                                        responseText={result.response}
                                                        renderVisualization={renderVisualization}
                                                        visualizationRef={visualizationRef}
                                                    />
                                                </div>
                                            ) : (
                                                <div
                                                    className="flex-1 flex flex-col items-center justify-center text-center p-12"
                                                    style={{ background: 'linear-gradient(180deg, var(--card-bg) 0%, var(--bg-tertiary) 100%)' }}
                                                >
                                                    {loading ? (
                                                        <div className="space-y-6 max-w-sm">
                                                            <div className="relative mx-auto w-20 h-20">
                                                                <div className="absolute inset-0 border-4 rounded-full" style={{ borderColor: 'var(--card-border)' }}></div>
                                                                <div className="absolute inset-0 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
                                                                <div className="absolute inset-0 flex items-center justify-center">
                                                                    <Activity className="h-6 w-6 text-blue-600 animate-pulse" />
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <h4 className="font-semibold mb-1" style={{ color: 'var(--accent-text)' }}>Analyzing Data</h4>
                                                                <p className="text-sm" style={{ color: 'var(--accent-text-subtle)' }}>{loadingStatus || 'Processing your request...'}</p>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="max-w-md">
                                                            <div className={`mx-auto w-24 h-24 rounded-2xl ${activeTool.bgColor} flex items-center justify-center mb-6`}>
                                                                <activeTool.icon className={`h-10 w-10 ${activeTool.color} opacity-80`} />
                                                            </div>
                                                            <h4 className="font-bold text-lg mb-2" style={{ color: 'var(--accent-text)' }}>Ready to run analysis</h4>
                                                            <p className="text-sm leading-relaxed mb-5" style={{ color: 'var(--accent-text-subtle)' }}>
                                                                Configure your filters in the panel and tap <span className="font-semibold" style={{ color: 'var(--accent-text)' }}>Run Analysis</span> to generate insights.
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {!isResultFocusMode && (
                                <div className="flex items-center justify-center gap-2 mt-2 mb-1">
                                    {['Config', 'Result'].map((label, idx) => (
                                        <button
                                            key={`active-panel-dot-${label}`}
                                            type="button"
                                            onClick={() => scrollToActivePanel(idx)}
                                            aria-label={`Go to ${label} panel`}
                                            className={`h-2 rounded-full transition-all ${mobileActivePanelIndex === idx ? 'w-5 bg-blue-500' : 'w-2 bg-gray-300'}`}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="hidden lg:flex flex-col lg:flex-row gap-8 animate-in fade-in slide-in-from-bottom-4 duration-300">
                            {/* Input Panel */}
                            {!isResultFocusMode && (
                                <ToolConfigPanel
                                    activeTool={activeTool}
                                    formData={formData}
                                    metadata={metadata}
                                    metadataLoading={metadataLoading}
                                    metadataSource={metadataSource}
                                    loading={loading}
                                    handleInputChange={handleInputChange}
                                    setFormData={setFormData}
                                    handleRunAnalysis={handleRunAnalysis}
                                />
                            )}

                            {/* Results Panel */}
                            <div className={isResultFocusMode ? "w-full" : "w-full lg:w-[66%] xl:w-[70%]"}>
                                <div className="relative h-full border border-gray-200 rounded-2xl shadow-sm overflow-hidden flex flex-col min-h-[560px]" style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>
                                    {error && (
                                        <div className="m-6 p-4 bg-red-50 border border-red-100 text-red-700 rounded-lg flex items-start animate-in fade-in slide-in-from-top-2">
                                            <AlertTriangle className="h-5 w-5 mr-3 mt-0.5 flex-shrink-0" />
                                            <div>
                                                <h4 className="font-semibold text-sm">Analysis Failed</h4>
                                                <p className="text-sm opacity-90">{error}</p>
                                            </div>
                                        </div>
                                    )}

                                    {result ? (
                                        <div className="flex-1 flex flex-col p-6 animate-in fade-in duration-500">
                                            <ResultsHeaderToolbar
                                                result={result}
                                                error={error}
                                                handleSaveReport={handleSaveReport}
                                                reportStatus={reportStatus}
                                                setReportStatus={setReportStatus}
                                                onNewAnalysis={() => setIsResultFocusMode(prev => !prev)}
                                                loading={loading}
                                                resultFeedback={resultFeedback}
                                                setResultFeedback={setResultFeedback}
                                                setShowRandomFeedbackPrompt={setShowRandomFeedbackPrompt}
                                                setShowFeedbackModal={setShowFeedbackModal}
                                                apiClient={apiClient}
                                                activeTool={activeTool}
                                                formData={formData}
                                            />
                                            {loading && loadingStatus && (
                                                <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50/80 px-4 py-3 flex items-center gap-3 animate-in fade-in">
                                                    <div className="w-4 h-4 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin" />
                                                    <div className="text-sm font-medium text-blue-800">{loadingStatus}</div>
                                                </div>
                                            )}
                                            <VisualizationPanel
                                                responseText={result.response}
                                                renderVisualization={renderVisualization}
                                                visualizationRef={visualizationRef}
                                            />
                                        </div>
                                    ) : (
                                        <div
                                            className="flex-1 flex flex-col items-center justify-center text-center p-12"
                                            style={{ background: 'linear-gradient(180deg, var(--card-bg) 0%, var(--bg-tertiary) 100%)' }}
                                        >
                                            {loading ? (
                                                <div className="space-y-6 max-w-sm">
                                                    <div className="relative mx-auto w-20 h-20">
                                                        <div className="absolute inset-0 border-4 rounded-full" style={{ borderColor: 'var(--card-border)' }}></div>
                                                        <div className="absolute inset-0 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
                                                        <div className="absolute inset-0 flex items-center justify-center">
                                                            <Activity className="h-6 w-6 text-blue-600 animate-pulse" />
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <h4 className="font-semibold mb-1" style={{ color: 'var(--accent-text)' }}>Analyzing Data</h4>
                                                        <p className="text-sm" style={{ color: 'var(--accent-text-subtle)' }}>{loadingStatus || 'Processing your request...'}</p>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="max-w-md space-y-4">
                                                    <div className={`mx-auto w-24 h-24 rounded-2xl ${activeTool.bgColor} flex items-center justify-center mb-6`}>
                                                        <activeTool.icon className={`h-10 w-10 ${activeTool.color} opacity-80`} />
                                                    </div>
                                                    <h3 className="text-xl font-bold" style={{ color: 'var(--accent-text)' }}>Ready to Analyze</h3>
                                                    <p className="leading-relaxed" style={{ color: 'var(--accent-text-subtle)' }}>
                                                        Configure the parameters on the left and click "Run Analysis" to generate insights using our AI models.
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
            {/* Feedback Modal */}
            {
                showFeedbackModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
                        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200">
                            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                                <h3 className="font-bold text-gray-900">Provide Feedback</h3>
                                <button
                                    onClick={() => setShowFeedbackModal(false)}
                                    className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="p-5">
                                <p className="text-sm text-gray-600 mb-4">
                                    Help us improve this analysis tool by sharing your thoughts or reporting improper results.
                                </p>
                                <textarea
                                    className="w-full h-32 p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-sm resize-none"
                                    placeholder="Describe the issue or suggestion..."
                                    value={feedbackText}
                                    onChange={(e) => setFeedbackText(e.target.value)}
                                    autoFocus
                                />
                            </div>
                            <div className="p-5 bg-gray-50 flex justify-end gap-3">
                                <button
                                    onClick={() => setShowFeedbackModal(false)}
                                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleFeedbackSubmit}
                                    disabled={!feedbackText.trim()}
                                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Submit Feedback
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Random Feedback Prompt (Bottom Right Notification) */}
            {
                showRandomFeedbackPrompt && !showFeedbackModal && (
                    <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-5 fade-in duration-500">
                        <div className="bg-white rounded-xl shadow-2xl border border-gray-200 max-w-sm overflow-hidden">
                            {/* Header */}
                            <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-100 flex items-center justify-between">
                                <div className="flex items-center">
                                    <div className="p-2 bg-blue-500/10 rounded-lg mr-3">
                                        <MessageSquare className="w-5 h-5 text-blue-600" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-gray-900 text-sm">Quick Feedback</h4>
                                        <p className="text-xs text-gray-500">How was this analysis?</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowRandomFeedbackPrompt(false)}
                                    className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-white transition-colors"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>

                            {/* Quick Actions */}
                            <div className="p-4 space-y-3">
                                <div className="flex gap-2">
                                    <button
                                        onClick={async () => {
                                            setResultFeedback('up');
                                            try {
                                                await apiClient.post('/feedback/intelligence', {
                                                    tool_id: activeTool.id,
                                                    is_positive: true,
                                                    query: formData,
                                                    response: result?.response || ''
                                                });
                                            } catch (err) {
                                                console.error('Failed to submit feedback:', err);
                                            }
                                            setShowRandomFeedbackPrompt(false);
                                        }}
                                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg transition-colors font-medium text-sm border border-green-200"
                                    >
                                        <ThumbsUp className="w-4 h-4" />
                                        Helpful
                                    </button>
                                    <button
                                        onClick={async () => {
                                            setResultFeedback('down');
                                            try {
                                                await apiClient.post('/feedback/intelligence', {
                                                    tool_id: activeTool.id,
                                                    is_positive: false,
                                                    query: formData,
                                                    response: result?.response || ''
                                                });
                                            } catch (err) {
                                                console.error('Failed to submit feedback:', err);
                                            }
                                            setShowRandomFeedbackPrompt(false);
                                        }}
                                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg transition-colors font-medium text-sm border border-red-200"
                                    >
                                        <ThumbsDown className="w-4 h-4" />
                                        Not Helpful
                                    </button>
                                </div>

                                <button
                                    onClick={() => {
                                        setShowRandomFeedbackPrompt(false);
                                        setShowFeedbackModal(true);
                                    }}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-lg transition-colors text-sm border border-gray-200"
                                >
                                    <MessageSquare className="w-4 h-4" />
                                    Add Comment
                                </button>
                            </div>

                            {/* Footer */}
                            <div className="px-4 pb-3">
                                <p className="text-xs text-gray-400 text-center">
                                    Your feedback helps us improve 🚀
                                </p>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default MaintenanceIntelligence;
